import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import Script from 'next/script';
import { useMemo } from 'react';
import type {
  CardContent,
  CardGroupContent,
  SectionStateConstraintsList,
} from '@/contentful';
import type { FAQInfoAccordionContent } from '../../FAQInfoAccordion/FAQInfoAccordion';
import type { SectionContent } from '../../GenericComponent/GenericComponent';
import type { SectionType } from '../../Section/Section';
import type {
  AccordionContent,
  GlobalFAQContent,
} from '../../TopLevelComponents/GlobalFAQ/GlobalFAQ';

interface Props {
  sections: SectionType[];
}

const isGlobalFAQ = (content: SectionContent): content is GlobalFAQContent => {
  return content?.sys?.contentType?.sys?.id === 'globalFaq';
};
const isFAQInfoAccordion = (
  content: SectionContent
): content is FAQInfoAccordionContent => {
  return content?.sys?.contentType?.sys?.id === 'faqinfoAccordion';
};

const isLocationConstraint = (
  // biome-ignore lint/suspicious/noExplicitAny: SectionType doesn't include SectionStateConstraintsList
  content: any
): content is SectionStateConstraintsList => {
  return Boolean(content?.fields?.constraints);
};

const getSectionContent = (sections: SectionType[]): SectionContent[] => {
  return sections.flatMap((section) => section.fields.content);
};
const getGlobalFAQs = (content: SectionContent[]): GlobalFAQContent[] => {
  return content.filter(isGlobalFAQ);
};
const getFAQInfoAccordions = (content: SectionContent[]) => {
  return content.filter(isFAQInfoAccordion);
};

const getDefaultContent = (content: SectionContent[]) => {
  return content
    .filter(isLocationConstraint)
    .flatMap(
      (sectionContent) =>
        (sectionContent as SectionStateConstraintsList).fields.default
    );
};

const getGlobalFAQsData = (globalFAQs: GlobalFAQContent[]) => {
  const faqs: AccordionContent[] = [];

  globalFAQs.forEach((globalFAQ) => {
    globalFAQ.fields.faqs.forEach((faq) => {
      faqs.push(faq);
    });
  });

  return faqs.map((faq) => ({
    question: faq.fields.summary,
    answer: documentToPlainTextString(faq.fields.details),
  }));
};

const getFAQInfoAccordionsData = (
  faqinfoAccordions: FAQInfoAccordionContent[]
) => {
  const faqs: CardGroupContent[] = [];

  faqinfoAccordions.forEach((faqinfoAccordion) => {
    faqinfoAccordion.fields.expandableSections.forEach((faq) => {
      faqs.push(faq);
    });
  });

  const getAnswer = (cards: CardContent[]) => {
    const answers: string[] = [];

    cards.forEach((card) => {
      if (card.fields.richDescription) {
        answers.push(documentToPlainTextString(card.fields.richDescription));
      } else if (card.fields.description) {
        answers.push(card.fields.description);
      }
    });

    return answers.join('\n');
  };

  return faqs.map((faq) => ({
    question: faq.fields.title,
    answer: getAnswer(faq.fields.cards),
  }));
};

const getFAQData = (sections: SectionType[]) => {
  const standardContent = getSectionContent(sections);
  const sectionContent = [
    ...standardContent,
    ...getDefaultContent(standardContent),
  ];

  const globalFAQs = getGlobalFAQs(sectionContent);
  const faqinfoAccordions = getFAQInfoAccordions(sectionContent);

  const globalFAQsData = getGlobalFAQsData(globalFAQs);
  const faqinfoAccordionsData = getFAQInfoAccordionsData(faqinfoAccordions);

  return [...globalFAQsData, ...faqinfoAccordionsData];
};

export default function FAQPageSchema({ sections }: Props) {
  const faqs = useMemo(() => getFAQData(sections), [sections]);
  const schema = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      }),
    [faqs]
  );

  if (faqs.length === 0) return null;
  return (
    <Script type='application/ld+json' strategy='beforeInteractive'>
      {`${schema}`}
    </Script>
  );
}
